import React, { useCallback } from 'react';
import { SentryErrorBoundary } from 'utils/sentry';
import { IconSize, VdsIcon } from '@el8/vital';
import { FeedbackType, OpenSupportDialogPayload, useOpenSupportDialog } from 'ui-modules/support';
import {
  useOpenRestoreDeletedDocumentsDialog,
  useOpenRestoreDeletedPatientsDialog,
} from 'ui-modules/navbar';
import { logEvent } from 'utils/amplitude';
import CaseIntakeModal from 'views/support/case-intake/CaseIntakeModal';
import { logVisitTrainingCenter } from 'views/onboarding/amplitudeTaxonomy';
import { NavbarItem, NavbarItemDropdownProps } from '../../components/NavbarItem';
import styles from './INeedHelpNavItem.less';
import { RestoreDeletedDocumentsDialog } from '../../dialogs/RestoreDeletedDocumentsDialog';
import { RestoreDeletedPatientsDialog } from '../../dialogs/RestoreDeletedPatientsDialog';

const getMenuItems = ({
  openTourguide,
  openSupportDialog,
  openRestoreDeletedDocumentsDialog,
  openRestoreDeletedPatientsDialog,
}: {
  openTourguide: VoidFunction;
  openRestoreDeletedDocumentsDialog: VoidFunction;
  openRestoreDeletedPatientsDialog: VoidFunction;
  openSupportDialog: (payload: OpenSupportDialogPayload) => void;
}): NavbarItemDropdownProps['items'] => {
  const isPracticeHome = el8Globals.APP === 'home';
  const isPatientChart = el8Globals.APP === 'chart';
  const isOnboarding = el8Globals.PAGE_NAME === 'onboarding';

  const result: NavbarItemDropdownProps['items'] = [
    {
      'data-testid': 'navbar-ineedhelp-search-help-center-link',
      type: 'link' as const,
      href: 'https://help.elationhealth.com/s/',
      text: 'Search our Help Center',
      divider: true,
    },
    {
      'data-testid': 'navbar-ineedhelp-training-center-link',
      type: 'link' as const,
      href: 'https://www.elationhealth.com/training-center/',
      text: 'Explore our Training Center',
      onClick: () => logVisitTrainingCenter('I need help dropdown'),
    },
  ];

  if (!isOnboarding) {
    result.push({
      'data-testid': 'navbar-ineedhelp-elation-university-link',
      type: 'link' as const,
      href: 'https://www.elationhealth.com/elation-university/',
      text: 'Learn on Elation University',
    });
  }

  if (isPatientChart || isPracticeHome) {
    result.push({
      'data-testid': 'navbar-ineedhelp-show-map-button',
      type: 'button' as const,
      text: 'Show me a map of this page',
      onClick: openTourguide,
      id: 'info-splash-trigger',
      divider: true,
    });
  }

  if (el8Globals.FEATURES.CustomerCasePortal) {
    result.push({
      'data-testid': 'navbar-ineedhelp-case-portal-link',
      type: 'link' as const,
      href: 'https://sso.app.elationemr.com/home/salesforce/0oai9au58pBfRE2H15d7/46',
      text: 'View my support tickets',
      icon: <VdsIcon icon="external-link" size={IconSize.XSmall} />,
    });
  }

  result.push({
    'data-testid': 'navbar-ineedhelp-regular-feedback-button',
    type: 'button' as const,
    icon: <VdsIcon icon="message" size={IconSize.XSmall} />,
    text: 'Contact Elation Support',
    onClick: () => {
      if (el8Globals.FEATURES.FiniChatWidget) {
        // Fini has no public open() API; trigger via its hidden native launcher button
        const finiButton = document.querySelector<HTMLElement>('.fw-cw-button');
        if (finiButton) {
          finiButton.click();
        } else {
          // Fini script hasn't loaded or failed; fall back to the support dialog
          openSupportDialog({ feedbackType: FeedbackType.RegularFeedbackType });
        }
      } else {
        openSupportDialog({ feedbackType: FeedbackType.RegularFeedbackType });
      }
    },
  });

  if (isPatientChart) {
    result.push({
      'data-testid': 'navbar-ineedhelp-deleted-note-button',
      type: 'button' as const,
      text: 'I accidentally deleted a note or report',
      icon: <VdsIcon icon="warning-filled" size={IconSize.XSmall} />,
      bold: true,
      onClick: openRestoreDeletedDocumentsDialog,
    });
  }

  if (isPracticeHome && el8Globals.FEATURES.RestoreDeletedPts) {
    result.push({
      'data-testid': 'navbar-ineedhelp-deleted-patient-button',
      type: 'button' as const,
      text: 'Restore a deleted chart',
      icon: <VdsIcon icon="warning-filled" size={IconSize.XSmall} />,
      bold: true,
      onClick: openRestoreDeletedPatientsDialog,
    });
  }

  return result;
};

export const INeedHelpNavItem = (): JSX.Element | null => {
  const openSupportDialog = useOpenSupportDialog();

  const openRestoreDeletedDocumentsDialog = useOpenRestoreDeletedDocumentsDialog();
  const openRestoreDeletedPatientsDialog = useOpenRestoreDeletedPatientsDialog();

  const openTourguide = useCallback(() => {
    if (el8Globals.APP === 'home' && el8.tourguide?.homeInfoSplash) {
      el8.tourguide.homeInfoSplash.show();
    } else if (el8Globals.APP === 'chart' && el8.tourguide?.chartInfoSplash) {
      el8.tourguide.chartInfoSplash.show();
    }
  }, []);

  if (!el8Globals.IS_LAB_VENDOR && el8Globals.EMR_TYPE === 'passport') {
    return (
      <NavbarItem
        type="link"
        href="/support/"
        icon={<VdsIcon icon="help-filled" size={IconSize.XXSmall} className={styles.helpIcon} />}
        data-testid="navbar-ineedhelp-button"
      >
        I need help
      </NavbarItem>
    );
  }

  if (el8Globals.IS_LAB_VENDOR) {
    return null;
  }

  return (
    <>
      <NavbarItem
        type="dropdown"
        items={getMenuItems({
          openTourguide,
          openSupportDialog,
          openRestoreDeletedDocumentsDialog,
          openRestoreDeletedPatientsDialog,
        })}
        icon={<VdsIcon icon="help-filled" size={IconSize.XXSmall} className={styles.helpIcon} />}
        data-testid="navbar-ineedhelp-button"
        onClick={(): void => logEvent('open: navigation help menu')}
      >
        I need help
      </NavbarItem>
      <CaseIntakeModal />
      <RestoreDeletedDocumentsDialog />
      {el8Globals.FEATURES.RestoreDeletedPts && (
        <SentryErrorBoundary>
          <RestoreDeletedPatientsDialog />
        </SentryErrorBoundary>
      )}
    </>
  );
};
